import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useOwner } from '../contexts/OwnerContext'
import ErrorPage from '../components/ErrorPage'

interface ProtectedRouteProps {
  children: React.ReactNode
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isOwnerValid, isLoading, error } = useOwner()
  const [unauthorized, setUnauthorized] = useState(false)

  // Escutar evento global de 401 (disparado pelo RTK Query)
  useEffect(() => {
    const handleUnauthorized = (event: Event) => {
      const customEvent = event as CustomEvent
      // Auth unauthorized event captured
      setUnauthorized(true)
    }

    window.addEventListener('auth:unauthorized', handleUnauthorized)

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized)
    }
  }, [])

  // PRIORIDADE 1: Mostrar erro 401 (Owner não encontrado pelo middleware)
  if (unauthorized) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4 transition-colors duration-300"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full"
        >
          <div 
            className="p-8 rounded-2xl shadow-2xl text-center"
            style={{ 
              backgroundColor: 'var(--bg-card)',
              border: '2px solid #ef4444'
            }}
          >
            <div 
              className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
            >
              <svg 
                className="w-12 h-12" 
                fill="none" 
                stroke="#ef4444" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                />
              </svg>
            </div>
            
            <h2 
              className="text-3xl font-bold mb-4"
              style={{ color: '#ef4444' }}
            >
              🚫 Acesso Negado
            </h2>
            
            <p 
              className="text-lg mb-6"
              style={{ color: 'var(--text-secondary)' }}
            >
              Você não possui permissões válidas para acessar esta funcionalidade.
            </p>
            
            <div 
              className="p-4 rounded-lg mb-6"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.05)' }}
            >
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-primary)' }}
              >
                ⚠️ Motivo: Owner não identificado
              </p>
              <p 
                className="text-xs mt-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                É obrigatório estar associado a uma empresa válida para acessar o sistema.
              </p>
            </div>
            
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:opacity-90"
              style={{ 
                backgroundColor: '#ef4444',
                color: 'white'
              }}
            >
              Voltar para Início
            </button>
          </div>
        </motion.div>
      </div>
    )
  }

  // PRIORIDADE 2: Loading
  if (isLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{
          backgroundColor: 'var(--bg-primary)',
          color: 'var(--text-primary)'
        }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--primary)' }}></div>
          <p className="text-lg">Verificando autenticação...</p>
        </div>
      </div>
    )
  }

  // PRIORIDADE 3: Owner inválido (inicial)
  if (!isOwnerValid) {
    return (
      <ErrorPage
        title="Acesso Negado"
        message="Você não tem permissão para acessar este sistema. É necessário um token de autenticação válido."
        showRetry={false}
      />
    )
  }

  // Show error page if there's an error
  if (error) {
    return (
      <ErrorPage
        title="Erro de Autenticação"
        message={`Falha na autenticação: ${error}`}
        showRetry={false}
      />
    )
  }

  // Render children if owner is valid
  return <>{children}</>
}

export default ProtectedRoute
